import { loadGsap } from './motion-library.js';
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

// Reveal content units rather than section shells: transforming a section can
// trap fixed controls and interfere with sticky elements inside it.
const TARGETS = [
  '[data-scroll-reveal]', '.titan-section-heading', '.product-card', '.collection-card',
  '.resource-card', '.blog-post-item', '.titan-promos__offer', '.titan-coaches__coach',
  '.titan-cages__membership', '.rte', 'figure', 'h1', 'h2', 'h3', 'p',
].join(',');
const EXCLUDED = [
  '[data-scroll-reveal="off"]', '[data-tb-reveal]', '[data-collab-reveal]',
  '[data-no-reveal]', '[hidden]', '[inert]', 'form', 'dialog', 'theme-drawer',
  '[data-text-effect]', '[data-animate]',
  'slideshow-component', 'floating-shop-links', 'sticky-add-to-cart',
  'product-form-component', 'titan-collection-heading', '.product-information',
  '.cart-page', '.tb-hero', '[data-collab-sticky]', '.titan-side-mesh',
].join(',');

class StorefrontScrollReveal {
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  #observer;
  #mutations;
  #gsap;
  #version = 0;
  #frame = 0;
  #seen = new WeakSet();
  #pending = new Set();
  #active = new Map();

  constructor() {
    this.#motion.addEventListener('change', this.#start);
    scrollContainerMediaQuery.addEventListener('change', this.#start);
    window.addEventListener('pagehide', this.#stop);
    window.addEventListener('pageshow', this.#start);
    document.addEventListener('focusin', this.#finishInteraction, { capture: true });
    document.addEventListener('pointerdown', this.#finishInteraction, { capture: true, passive: true });
    this.#start();
  }

  #start = async () => {
    this.#stop();
    if (this.#motion.matches || !('IntersectionObserver' in window)) return;
    const version = this.#version;
    try {
      this.#gsap = await loadGsap();
      if (version !== this.#version || this.#motion.matches) return;
      this.#observer = new IntersectionObserver(this.#onIntersection, {
        root: getIntersectionRoot(),
        rootMargin: '0px 0px 48px 0px',
        threshold: 0,
      });
      this.#scan();
      // Child-list observation covers infinite scrolling, filtered results and
      // section replacement, without reacting to our own animation styles.
      this.#mutations = new MutationObserver((records) => {
        // Text counters and price-label updates must not trigger document scans.
        if (!records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) => node instanceof Element))) return;
        if (!this.#frame) this.#frame = requestAnimationFrame(() => {
          this.#frame = 0;
          this.#scan();
        });
      });
      for (const root of this.#roots()) this.#mutations.observe(root, { childList: true, subtree: true });
    } catch {
      // No CSS pre-hides content. A failed library leaves a usable storefront.
      this.#stop();
    }
  };

  #roots() {
    return Array.from(document.querySelectorAll('#MainContent, #GiftCardMain, .page-wrapper > footer, body > footer'));
  }

  #scan() {
    for (const element of this.#pending) {
      if (!element.isConnected) {
        this.#observer?.unobserve(element);
        this.#pending.delete(element);
      }
    }
    for (const [element, context] of this.#active) {
      if (!element.isConnected) {
        context.revert();
        this.#active.delete(element);
      }
    }
    const viewport = getIntersectionRoot()?.getBoundingClientRect();
    const bottom = viewport?.bottom ?? innerHeight;
    for (const root of this.#roots()) {
      for (const element of root.querySelectorAll(TARGETS)) {
        if (!(element instanceof HTMLElement) || this.#seen.has(element) || this.#pending.has(element) || this.#active.has(element)) continue;
        if (element.closest(EXCLUDED) || element.parentElement?.closest(TARGETS) || element.querySelector('form, sticky-add-to-cart, [data-collab-sticky]')) continue;
        const style = getComputedStyle(element);
        if (style.position === 'fixed' || style.position === 'sticky' || style.visibility === 'hidden' || style.display === 'none' || style.animationName !== 'none') continue;
        const bounds = element.getBoundingClientRect();
        if (!bounds.width || !bounds.height) continue;
        // Initial viewport, restored scroll positions and newly inserted visible
        // cards remain visible; they must never disappear after a late JS load.
        if (bounds.top <= bottom + 48) {
          this.#seen.add(element);
          continue;
        }
        this.#pending.add(element);
        this.#observer?.observe(element);
      }
    }
  }

  #onIntersection = (entries) => {
    let index = 0;
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const element = entry.target;
      this.#observer?.unobserve(element);
      this.#pending.delete(element);
      this.#seen.add(element);
      if (!element.isConnected || this.#motion.matches || element.contains(document.activeElement)) continue;
      let context;
      context = this.#gsap.context(() => {
        this.#gsap.fromTo(element, { opacity: 0.12, y: 20 }, {
          opacity: 1, y: 0, duration: 0.65, delay: (index++ % 3) * 0.06,
          ease: 'power3.out',
          onComplete: () => {
            context?.revert();
            this.#active.delete(element);
          },
        });
      });
      this.#active.set(element, context);
    }
  };

  #finishInteraction = (event) => {
    if (!(event.target instanceof Node)) return;
    for (const element of this.#pending) {
      if (!element.contains(event.target)) continue;
      this.#observer?.unobserve(element);
      this.#pending.delete(element);
      this.#seen.add(element);
    }
    for (const [element, context] of this.#active) {
      if (!element.contains(event.target)) continue;
      context.revert();
      this.#active.delete(element);
    }
  };

  #stop = () => {
    this.#version++;
    this.#observer?.disconnect();
    this.#mutations?.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#pending.clear();
    for (const context of this.#active.values()) context.revert();
    this.#active.clear();
  };
}

new StorefrontScrollReveal();
